import {
  AutoMarkComponent,
  InteractiveComponent
} from '../models/LessonComponent';
import { isNil, isNull } from 'lodash';

import { ApiPortfolioState } from '../../dashboard/models/ApiPortfolioState';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Unstable_Grid2';
import { LessonComponentResultPanel } from './LessonComponentResultPanel';
import LessonResponseService from '../services/LessonResponseService';
import React from 'react';
import { Typography } from '@mui/material';
import styles from '../styles/LessonComponentSubmissionPanel.module.css';
import { useLessonComponentContext } from '../models/LessonComponentContext';
import { useLessonContext } from '../models/LessonContext';

interface LessonComponentSubmissionPanelProps<T> {
  points: number;
  assignedPoints: number;
  component: AutoMarkComponent;
  answerModified: boolean;
  lessonState: ApiPortfolioState;
}

export function LessonComponentSubmissionPanel<T extends InteractiveComponent>(
  props: LessonComponentSubmissionPanelProps<T>
) {
  const responsePattern = props.component.answer || null;

  /* Enable/disable 'Save and Continue' button depending on the user's response */
  const hasAnswer = LessonResponseService.canSaveAnswer(
    props.component,
    responsePattern
  );

  const {
    lessonType,
    finalized,
    quizLocked,
    assessmentSubmitted,
    portfolioLocked
  } = useLessonContext();
  const { correct, reattempt, showFeedback, handleReattempt, handleSubmit } =
    useLessonComponentContext();

  const showChangeAnswer = () => {
    /* Show 'ChangeAnswer' button in these conditions */
    /* Quiz lesson type */
    if (lessonType === 'quiz') {
      /* Only when not finalized and not answered yet */
      if (!finalized && !isNull(correct) && !quizLocked) return true;
    }
    /* Assessment lesson type */
    if (lessonType === 'assessment') {
      /* Only when not finalized and not submitted yet */
      if (!finalized && !assessmentSubmitted) {
        if (
          /* This is extra check for other than 'work required' states and not answered yet */
          (props.lessonState !== ApiPortfolioState.AssessmentRetry &&
            !isNull(correct)) ||
          /* In the 'work required' state, if the answer is wrong or if the answer is correct and also the learner modifies the answer */
          (props.lessonState === ApiPortfolioState.AssessmentRetry &&
            (!correct || (correct && props.answerModified)))
        ) {
          return true;
        }
      }
    }
    return false;
  };

  return (
    <Grid
      container
      direction="column"
      alignItems="center"
      justifyContent="center"
    >
      {correct === false &&
        (!reattempt || finalized || (lessonType === 'quiz' && quizLocked)) && (
          <>
            {showFeedback && (
              <Grid>
                <LessonComponentResultPanel
                  correct={correct}
                  message={props.component.settings?.incorrectFeedback}
                  points={props.points}
                  assignedPoints={props.assignedPoints}
                />
              </Grid>
            )}
            {!finalized && lessonType === 'lesson' && (
              <Grid className={styles.submissionPanel}>
                <Button
                  color="secondary"
                  variant="contained"
                  onClick={handleReattempt}
                  disabled={portfolioLocked}
                >
                  Reattempt Question
                </Button>
              </Grid>
            )}
          </>
        )}
      {!isNil(correct) && showChangeAnswer() && !reattempt && (
        <Grid className={styles.submissionPanel}>
          <Button
            color="secondary"
            variant="contained"
            onClick={handleReattempt}
            disabled={portfolioLocked}
          >
            Change Answer
          </Button>
        </Grid>
      )}
      {correct === true && showFeedback && (
        <Grid>
          <LessonComponentResultPanel
            correct={correct}
            message={props.component.settings?.correctFeedback}
            points={props.points}
            assignedPoints={props.assignedPoints}
          />
        </Grid>
      )}

      {(isNull(correct) || reattempt) &&
        (!finalized && !assessmentSubmitted && !quizLocked ? (
          <>
            {props.assignedPoints > 0 && (
              <Grid className={styles.points}>
                <Typography display="inline" variant="subtitle2" align="center">
                  {`This question is worth ${props.assignedPoints} point${
                    props.assignedPoints !== 1 ? 's' : ''
                  }`}
                </Typography>
              </Grid>
            )}
            <Grid className={styles.submissionPanel}>
              <Button
                color="primary"
                variant="contained"
                disabled={!hasAnswer}
                onClick={handleSubmit}
              >
                Save and Continue
              </Button>
            </Grid>
          </>
        ) : (
          isNil(responsePattern) && (
            <Grid className={styles.submissionPanel}>
              <LessonComponentResultPanel
                correct={correct}
                message="You didn't answer this question"
                points={props.points}
                assignedPoints={props.assignedPoints}
              />
            </Grid>
          )
        ))}
    </Grid>
  );
}
